import React from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useUIStore } from '../../store/ui'
import { useAuthStore } from '../../store/auth'
import { clsx } from 'clsx'
import {
  Home,
  BarChart3,
  UtensilsCrossed,
  Search,
  Target,
  Users,
  User,
  Settings,
  LogOut,
  X,
  MessageCircle,
} from 'lucide-react'

const Sidebar: React.FC = () => {
  const { t } = useTranslation()
  // const location = useLocation()
  const { isSidebarOpen, setSidebarOpen, isMobile } = useUIStore()
  const { user, logout } = useAuthStore()

  const navigationItems = [
    {
      name: t('navigation.dashboard'),
      href: '/dashboard',
      icon: Home,
    },
    {
      name: t('navigation.meals'),
      href: '/meals',
      icon: UtensilsCrossed,
    },
    {
      name: '음식 검색',
      href: '/food',
      icon: Search,
    },
    {
      name: t('navigation.nutrition'),
      href: '/nutrition',
      icon: BarChart3,
    },
    {
      name: t('navigation.quests'),
      href: '/quests',
      icon: Target,
    },
    {
      name: 'AI 영양사',
      href: '/chat',
      icon: MessageCircle,
    },
    {
      name: t('navigation.social'),
      href: '/social',
      icon: Users,
    },
  ]

  const bottomItems = [
    {
      name: t('navigation.profile'),
      href: '/profile',
      icon: User,
    },
    {
      name: t('navigation.settings'),
      href: '/settings',
      icon: Settings,
    },
  ]

  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const handleLogout = () => {
    logout()
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  return (
    <div
      className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">NutriQuests</span>
          </div>
          
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                {user.avatar ? (
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-5 h-5 text-primary-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500">
                  Level {user.level} • {user.exp} EXP
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-gray-200 p-4 space-y-1">
          {bottomItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={handleNavClick}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.name}
            </NavLink>
          ))}
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Sidebar